/**
 * RegisterExport — printable controlled drug register (and spreadsheet copy)
 * for Pharmacy Council / regulator inspections.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Printer, Sheet, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { printReport, exportCSV } from '../../lib/reportExportUtils';
import { RegisterDeliveryStatus } from './RegisterDeliveryStatus';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';

const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

export const RegisterExport = () => {
  const [start, setStart] = useState(monthAgo);
  const [end, setEnd] = useState(today);
  const [busy, setBusy] = useState('');
  const [includeStamp, setIncludeStamp] = useState(true);
  const [reminder, setReminder] = useState(false);

  const fetchRows = async () => {
    const { data } = await api.get('/controlled/register', { params: { start, end } });
    if (!data.register?.length) { toast.error('No approved controlled sales in those dates'); return null; }
    return data.register;
  };

  const run = (mode, forceStamp) => async () => {
    if (mode === 'print' && !includeStamp && forceStamp === undefined) { setReminder(true); return; }
    setReminder(false);
    setBusy(mode);
    try {
      const rows = await fetchRows();
      if (!rows) return;
      if (mode === 'print') {
        const pretty = (d) => d.split('-').reverse().join('/');
        const signOff = `
          <div data-testid="register-signoff" style="display:flex;justify-content:space-between;gap:48px;margin-top:48px;padding-top:4px;page-break-inside:avoid;">
            <div style="flex:1;">
              <div style="border-top:1px solid #111827;width:100%;margin-top:46px;"></div>
              <p style="font-size:11px;color:#111827;font-weight:600;margin-top:6px;">Pharmacist signature</p>
              <p style="font-size:10px;color:#6b7280;margin-top:10px;">Name: ______________________________&nbsp;&nbsp;&nbsp;Date: ______________</p>
              <p style="font-size:10px;color:#6b7280;margin-top:6px;">Pharmacy Council registration no.: ______________________________</p>
            </div>
            <div style="flex:0 0 220px;">
              <div style="border:1px dashed #9ca3af;border-radius:6px;height:90px;display:flex;align-items:center;justify-content:center;">
                <p style="font-size:10px;color:#9ca3af;text-transform:uppercase;letter-spacing:0.5px;">Official pharmacy stamp</p>
              </div>
            </div>
          </div>`;
        printReport(rows, `Controlled Drug Register ${pretty(start)} to ${pretty(end)}`, { entries: rows.length }, null, (forceStamp ?? includeStamp) ? signOff : '');
      } else {
        exportCSV(rows, `Controlled Drug Register ${start} to ${end}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not build the register');
    } finally {
      setBusy('');
    }
  };

  return (
    <><RegisterDeliveryStatus onSettings={setIncludeStamp}/><div className="rounded-xl border bg-muted/30 p-3 flex flex-wrap items-end gap-3" data-testid="register-export">
      <div>
        <Label className="text-[11px]">From</Label>
        <Input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} className="h-9 text-sm" data-testid="register-start" />
      </div>
      <div>
        <Label className="text-[11px]">To</Label>
        <Input type="date" value={end} min={start} max={today()} onChange={(e) => setEnd(e.target.value)} className="h-9 text-sm" data-testid="register-end" />
      </div>
      <Button size="sm" onClick={run('print')} disabled={!!busy} className="h-9 bg-amber-600 hover:bg-amber-500 text-white" data-testid="register-print">
        {busy === 'print' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Printer className="h-4 w-4 mr-1" />} Print register
      </Button>
      <Button size="sm" variant="outline" onClick={run('csv')} disabled={!!busy} className="h-9" data-testid="register-csv">
        {busy === 'csv' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sheet className="h-4 w-4 mr-1" />} Download spreadsheet
      </Button>
      <p className="text-[11px] text-muted-foreground basis-full">
        Every approved controlled sale in the dates above, with the customer, Ghana Card, prescriber and approving pharmacist.
      </p>
    </div><Dialog open={reminder} onOpenChange={setReminder}><DialogContent data-testid="register-stamp-reminder" closeProps={{'data-testid':'register-stamp-reminder-close'}}><DialogTitle>This register has no stamp box</DialogTitle><DialogDescription>Add the pharmacist signature and official stamp box for inspection day?</DialogDescription><div className="flex flex-wrap gap-2"><Button onClick={run('print',true)} data-testid="register-stamp-add-print">Add box & print</Button><Button variant="outline" onClick={run('print',false)} data-testid="register-stamp-continue">Print without box</Button><Button variant="ghost" onClick={()=>setReminder(false)} data-testid="register-stamp-cancel">Cancel</Button></div></DialogContent></Dialog></>
  );
};
